/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   getValidationSchema
 */

import type { MoveInDateInputProps } from '@zcode-ui/web-zumper-ui'
import type { SerializableDate } from '@zcode/bizos'
import invariant from 'tiny-invariant'
import * as Yup from 'yup'
import { emailValidator } from '../../../../packages/Inputs/validator/email'
import { fullNameValidator } from '../../../../packages/Inputs/validator/fullName'
import {
  moveInOptionalValidator,
  moveInRequiredValidator,
  moveInTextValidator,
} from '../../../../packages/Inputs/validator/moveIn'
import {
  phoneValidatorOptional,
  phoneValidatorRequired,
} from '../../../../packages/Inputs/validator/phone'
import {
  type TourTimeValidatorProps,
  getSelectedDateTimeValidator,
} from '../../../../packages/Inputs/validator/selectedDateTime'

interface Props extends TourTimeValidatorProps {
  floorplanRequired: boolean | undefined
  moveInRequired: boolean | undefined
  phoneNumberRequired: boolean | undefined
}

export interface SelectedDateAndTime {
  readonly selectedDate: ReturnType<Date['toDateString']>
  readonly selectedTime: Date | SerializableDate
}

export interface Values {
  email: string
  messageModified: boolean
  moveIn?: Date | string
  moveInText?: Parameters<MoveInDateInputProps['onSelectMoveInDate']>[1]
  msg: string
  name: string
  phone?: string
  saveSearch?: boolean
  selectedDateTime: SelectedDateAndTime | null
  selectedFloorplanId?: number | null | undefined
  selectedFloorplanBedroom: number | null
  selectedFloorplanMaxPrice: number | null
  selectedFloorplanMinPrice: number | null
  selectedListingId?: number | null
  unitId: number | undefined
}

let LAST_VALIDATOR:
  | {
      args: Props
      result: ReturnType<typeof _getValidationSchema>
    }
  | undefined
const KEYS_OF_PROPS = [
  'equalCTAIntent',
  'fetchingTourAvailability',
  'floorplanRequired',
  'moveInRequired',
  'phoneNumberRequired',
  'tourTimeOptionsByDate',
] as const satisfies ReadonlyArray<keyof Props>
declare const shouldBeEmpty: Exclude<keyof Props, (typeof KEYS_OF_PROPS)[number]>
false && (shouldBeEmpty satisfies never)

export const getValidationSchema = (args: Props) => {
  if (
    LAST_VALIDATOR &&
    KEYS_OF_PROPS.every((key) => {
      invariant(LAST_VALIDATOR)
      return args[key] === LAST_VALIDATOR.args[key]
    })
  ) {
    return LAST_VALIDATOR.result
  }
  LAST_VALIDATOR = {
    args,
    result: _getValidationSchema(args),
  }
  return LAST_VALIDATOR.result
}

const _getValidationSchema = ({
  equalCTAIntent,
  fetchingTourAvailability,
  floorplanRequired,
  moveInRequired,
  phoneNumberRequired,
  tourTimeOptionsByDate,
}: Props) => {
  return Yup.object().shape({
    email: emailValidator.test(
      'not-proxy',
      'Must not be a zumperchat.com email address',
      (value) => !value?.match(/[@.]zumperchat\.com\.?$/i)
    ),
    messageModified: Yup.boolean(),
    moveIn: moveInRequired ? moveInRequiredValidator : moveInOptionalValidator,
    moveInText: moveInTextValidator,
    msg: Yup.string(),
    name: fullNameValidator,
    phone: phoneNumberRequired ? phoneValidatorRequired : phoneValidatorOptional,
    saveSearch: Yup.boolean(),
    selectedDateTime: getSelectedDateTimeValidator({
      equalCTAIntent,
      fetchingTourAvailability,
      tourTimeOptionsByDate,
    }),
    selectedFloorplanId: floorplanRequired ? Yup.number().required() : Yup.number().nullable(),
  })
}
